/**
 * ユーザー(権限)マスタ。users/{uid} に role(admin/operator/viewer)を持つ。
 * 削除はしない(active=false で無効化。Auth 側のアカウントは残る)。
 */
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import type { User as FirebaseUser } from 'firebase/auth';
import { getDb } from '@core/lib/firebase';
import type { Role, UserDoc } from '@core/lib/types';
import { createAuthUser } from './createAuthUser';

function withId<T>(snap: QueryDocumentSnapshot): T {
  return { id: snap.id, ...(snap.data() as object) } as T;
}

export async function fetchUsers(): Promise<UserDoc[]> {
  const snap = await getDocs(query(collection(getDb(), 'users'), orderBy('name')));
  return snap.docs.map((d) => withId<UserDoc>(d));
}

/** メール＋パスワードのアカウントを作る。2026-09-15 から画面では使っていない(管理者・閲覧者は Google の招待)。スタッフのログイン方式(P-12)で使う可能性があるため残す */
export async function createUser(input: {
  email: string;
  password: string;
  name: string;
  role: Role;
  op_id: string | null;
}): Promise<string> {
  const email = input.email.trim();
  const name = input.name.trim();
  if (!email || !email.includes('@')) throw new Error('メールアドレスを入力してください');
  if (input.password.length < 8) throw new Error('初期パスワードは8文字以上にしてください');
  if (!name) throw new Error('氏名を入力してください');

  const uid = await createAuthUser(email, input.password);
  await setDoc(doc(getDb(), 'users', uid), {
    email,
    name,
    role: input.role,
    active: true,
    op_id: input.role === 'operator' ? input.op_id : null,
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  });
  return uid;
}

export async function updateUser(
  id: string,
  patch: { name: string; role: Role; active: boolean; op_id: string | null },
): Promise<void> {
  if (!patch.name.trim()) throw new Error('氏名を入力してください');
  await updateDoc(doc(getDb(), 'users', id), {
    name: patch.name.trim(),
    role: patch.role,
    active: patch.active,
    op_id: patch.role === 'operator' ? patch.op_id : null,
    updated_at: serverTimestamp(),
  });
}

// ---- Googleアカウントの招待(2026-09-15) ----------------------------------
// 管理者・閲覧者は Google アカウントでログインする。uid はログインするまで分からないので、
// 先にメールアドレスで招待(user_invites/{小文字のメール})を作り、本人の初回ログインで users/{uid} に変える。
// ルール(firestore.rules)でもロール・氏名を招待から変えられないようにしている。

/** 招待できるロール。スタッフ(operator)のログイン方式は保留(P-12) */
export type InviteRole = 'admin' | 'viewer';

export type UserInvite = {
  id: string; // 小文字のメールアドレス
  email: string;
  name: string;
  role: InviteRole;
  invited_at?: unknown;
  invited_by?: string;
};

const normEmail = (email: string) => email.trim().toLowerCase();

export async function fetchInvites(): Promise<UserInvite[]> {
  const snap = await getDocs(collection(getDb(), 'user_invites'));
  return snap.docs.map((d) => withId<UserInvite>(d)).sort((a, b) => a.name.localeCompare(b.name, 'ja'));
}

export async function createInvite(input: { email: string; name: string; role: InviteRole; invitedBy: string }, existingUsers: UserDoc[]): Promise<void> {
  const email = normEmail(input.email);
  const name = input.name.trim();
  if (!name) throw new Error('氏名を入力してください');
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) throw new Error('メールアドレスの形式が正しくありません');
  if (existingUsers.some((u) => normEmail(u.email) === email)) throw new Error('このメールアドレスは既に登録されています');
  const ref = doc(getDb(), 'user_invites', email);
  if ((await getDoc(ref)).exists()) throw new Error('このメールアドレスは既に招待済みです');
  await setDoc(ref, { email, name, role: input.role, invited_at: serverTimestamp(), invited_by: input.invitedBy });
}

export async function deleteInvite(id: string): Promise<void> {
  await deleteDoc(doc(getDb(), 'user_invites', id));
}

/**
 * Google でログインした直後に呼ぶ(ログインボタンの操作の続き)。
 * users/{uid} が無く、そのメールアドレスの招待があれば、招待どおりに users を作って招待を消す。
 * 作ったら true。登録済み・招待なしは false(画面は「権限が未設定です」になる)
 */
export async function claimInvite(fb: FirebaseUser): Promise<boolean> {
  const db = getDb();
  const userRef = doc(db, 'users', fb.uid);
  if ((await getDoc(userRef)).exists()) return false;
  const email = normEmail(fb.email ?? '');
  if (!email || !fb.emailVerified) return false;
  const inviteRef = doc(db, 'user_invites', email);
  const invite = await getDoc(inviteRef);
  if (!invite.exists()) return false;
  const data = invite.data() as Omit<UserInvite, 'id'>;
  const batch = writeBatch(db);
  batch.set(userRef, {
    email,
    name: data.name,
    role: data.role,
    active: true,
    op_id: null,
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  });
  batch.delete(inviteRef);
  await batch.commit();
  return true;
}
